import { Mail } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { useLang } from "../i18n/LanguageProvider";

const team = [
  {
    id: "chen",
    name: "Jungang Chen",
    role: "Founder & Chairman",
    email: "chen@bonworldsingapore.com",
    background:
      "Established Bonworld in Singapore in 2022 after two decades leading a Greater-China private investment office. A patient allocator with a lifelong interest in the intersection of Chinese and European family capital.",
    skills: ["Cross-border structuring", "Private equity", "Family governance", "Mandarin · English"],
  },
  {
    id: "padel",
    name: "Alexander Padel",
    role: "Co-Founder & Managing Partner",
    email: "padel@bonworldsingapore.com",
    background:
      "Formerly head of alternative investments at a Swiss multi-family office. Architect of Bonworld's investment framework and lead partner on manager selection and deal underwriting.",
    skills: ["Alternatives", "Portfolio construction", "Manager selection"],
  },
  {
    id: "wyss",
    name: "Raymond Wyss",
    role: "Director, Portfolio Management & Finance",
    email: "wyss@bonworldsingapore.com",
    background:
      "CFA charterholder with a career spanning private banking in Zürich and Singapore. Oversees portfolio implementation, treasury and consolidated reporting for principal families.",
    skills: ["Portfolio management", "Corporate finance", "Risk & reporting", "Treasury"],
  },
  {
    id: "nagelsmann",
    name: "Thomas Nagelsmann",
    role: "Head of Investments",
    email: "nagelsmann@bonworldsingapore.com",
    background:
      "Former senior portfolio strategist at a European private bank. Leads capital-markets research, tactical asset allocation, and cross-asset positioning across all Bonworld mandates.",
    skills: ["Global macro", "Asset allocation", "Fixed income", "Public equities"],
  },
  {
    id: "bonhoeffer",
    name: "Dr. Peter Bonhoeffer",
    role: "Director of Finance & Head of Portfolio Strategy",
    email: "bonhoeffer@bonworldsingapore.com",
    background:
      "Doctorate in Economics from the University of Zürich. Twenty years advising European industrial families on capital deployment, private credit and long-duration real assets.",
    skills: ["Portfolio strategy", "Economics", "Private credit", "Real assets"],
  },
  {
    id: "siegman",
    name: "Philippe Siegman",
    role: "Partner, Family Governance",
    email: "siegman@bonworldsingapore.com",
    background:
      "Private-client counsel with two decades of practice across Geneva, London and Singapore. Advises principals on trust architecture, succession planning and philanthropic vehicles.",
    skills: ["Trust structuring", "Succession", "Philanthropy", "Regulatory"],
  },
  {
    id: "tan",
    name: "Mei Ling Tan",
    role: "Partner, Head of Public Markets",
    email: "tan@bonworldsingapore.com",
    background:
      "CFA charterholder with a 17-year track record across public equities and hedge-fund allocation for UHNW families. Leads Bonworld's manager due-diligence programme.",
    skills: ["Public equities", "Hedge funds", "Manager DD", "Mandarin · English · French"],
  },
  {
    id: "neumann",
    name: "Sophia Neumann",
    role: "Head of Legal & Compliance",
    email: "neumann@bonworldsingapore.com",
    background:
      "Formerly senior counsel at a Zürich-based private bank. Owns Bonworld's legal, regulatory and confidentiality framework across every jurisdiction we operate in.",
    skills: ["Regulatory affairs", "Trust law", "KYC / AML", "Data governance"],
  },
];

function initials(name) {
  return name
    .replace(/^Dr\.?\s+/i, "")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export default function Team() {
  const { t } = useLang();
  return (
    <>
      <PageHeader
        eyebrow={t("page.team.eyebrow")}
        title={t("page.team.title")}
        lede={t("page.team.lede")}
      />

      <section className="pb-24 lg:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {team.map((m, i) => (
              <article
                key={m.id}
                className={`card-lux p-8 lg:p-10 flex flex-col ${i % 2 === 1 ? "md:translate-y-6" : ""}`}
                data-testid={`team-member-${m.id}`}
              >
                <div className="flex items-start gap-6">
                  <div
                    className="shrink-0 w-16 h-16 border border-[var(--bw-gold)] flex items-center justify-center relative"
                    aria-hidden
                  >
                    <span className="font-serif-display italic text-gold text-xl">
                      {initials(m.name)}
                    </span>
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[var(--bw-gold)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-gold text-[10px] tracking-[0.32em] uppercase mb-2">
                      {m.role}
                    </div>
                    <h3 className="font-serif-display text-2xl lg:text-3xl text-white leading-tight">
                      {m.name}
                    </h3>
                  </div>
                </div>

                <p className="text-muted text-sm leading-relaxed mt-6">{m.background}</p>

                <div className="mt-6 flex flex-wrap gap-2">
                  {m.skills.map((s) => (
                    <span
                      key={s}
                      className="text-[10px] tracking-[0.18em] uppercase text-white/80 border border-[var(--bw-border)] px-3 py-1"
                    >
                      {s}
                    </span>
                  ))}
                </div>

                <div className="mt-8 pt-6 border-t border-[var(--bw-border)] flex items-center justify-between">
                  <a
                    href={`mailto:${m.email}`}
                    className="text-sm text-white hover:text-gold flex items-center gap-2"
                    data-testid={`team-email-${m.id}`}
                  >
                    <Mail size={14} className="text-gold" strokeWidth={1.4} />
                    {m.email}
                  </a>
                  <span className="text-[10px] tracking-[0.28em] uppercase text-muted">
                    Bonworld · Singapore
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[var(--bw-border)] py-20 bg-surface">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 text-center">
          <div className="eyebrow inline-flex mb-6">Advisory Circle</div>
          <p className="font-serif-display text-2xl lg:text-3xl text-white leading-[1.35] max-w-3xl mx-auto">
            Bonworld is supported by a confidential circle of legal, tax, philanthropic and
            operating advisors — introduced to a family only when a mandate calls for it.
          </p>
        </div>
      </section>
    </>
  );
}
